import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { UserCheck, Truck, AlertTriangle, Info, ExternalLink, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import CustomerMatchCandidatesDialog from "./CustomerMatchCandidatesDialog";
import QuoteMappingReview from "./QuoteMappingReview";

/**
 * QuoteConversionPanel — geïntegreerd paneel voor OfferteDetail (Fase 3B).
 *
 * REGEL 2 (één bron per gegeven):
 *   - Alle klantinfo uit `db.customers` via `GET /admin/customers/{id}`
 *   - Alle candidate-info uit `GET /admin/quotes/{id}/candidates`
 *   - Alle conversie-beslissingen uit `POST /admin/quotes/{id}/convert-to-shipment`
 *   - Alle mapping-info uit `GET /admin/shipments/{id}/mapping-report`
 *   - GEEN client-side afgeleide waarden of caches
 *
 * REGEL 1 (tenant-onafhankelijkheid): alle strings NL — geen hardcoded
 * 5999Cargo tokens.
 */
export default function QuoteConversionPanel({ quote, onQuoteChanged }) {
  const { hasPermission, isAdminOrOwner, user } = useAuth();
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [shipmentId, setShipmentId] = useState(quote?.converted_to_shipment_id || null);
  const [existingShipment, setExistingShipment] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [exceptionReason, setExceptionReason] = useState("");

  const canConvert = isAdminOrOwner() || user?.role === "manager" || user?.role === "webshop_manager";
  const canForceException = isAdminOrOwner();
  const canManageCustomerLink = canConvert;

  const quoteId = quote?.id;

  const loadLinkedCustomer = useCallback(async () => {
    if (!quote?.customer_id) {
      setLinkedCustomer(null);
      return;
    }
    try {
      const r = await adminApi.get(`/admin/customers/${quote.customer_id}`);
      setLinkedCustomer(r.data || null);
    } catch (e) {
      setLinkedCustomer(null);
    }
  }, [quote?.customer_id]);

  const checkExistingConversion = useCallback(async () => {
    if (!quoteId) return;
    setCheckingExisting(true);
    try {
      // Optionele endpoint om te zien of er al een primary shipment bestaat.
      // We gebruiken de convert-endpoint met een probing GET niet; ipv gebruiken
      // we quote.converted_to_shipment_id als hint.
      if (quote?.converted_to_shipment_id) {
        setShipmentId(quote.converted_to_shipment_id);
        const r = await adminApi.get(
          `/admin/shipping-labels/${quote.converted_to_shipment_id}`,
        );
        setExistingShipment(r.data || null);
      } else {
        setShipmentId(null);
        setExistingShipment(null);
      }
    } catch (e) {
      // silent — 404 wanneer nog niet geconverteerd is prima
    } finally {
      setCheckingExisting(false);
    }
  }, [quoteId, quote?.converted_to_shipment_id]);

  useEffect(() => {
    loadLinkedCustomer();
    checkExistingConversion();
  }, [loadLinkedCustomer, checkExistingConversion]);

  const handleConvert = async (forceException = false, reason = "") => {
    if (!canConvert) return;
    if (!quote?.customer_id) {
      toast.error(
        "Klant nog niet gekoppeld aan deze offerte — voer eerst klantmatching uit.",
      );
      return;
    }
    setConverting(true);
    setConvertResult(null);
    try {
      const payload = forceException
        ? { force_exception_copy: true, forced_reason: reason }
        : {};
      const r = await adminApi.post(
        `/admin/quotes/${quoteId}/convert-to-shipment`,
        payload,
      );
      const data = r.data || {};
      setConvertResult(data);
      if (data.created) {
        toast.success(
          data.conversion_role === "primary"
            ? `Concept-zending aangemaakt (${(data.shipment_id || "").slice(0, 8)}…)`
            : `Exception-copy shipment aangemaakt (${(data.shipment_id || "").slice(0, 8)}…)`,
        );
      } else if (data.already_exists) {
        toast.info(
          `Bestaand shipment gekoppeld (${(data.existing_shipment_id || "").slice(0, 8)}…)`,
        );
      }
      setShipmentId(data.shipment_id || data.existing_shipment_id || null);
      setExceptionOpen(false);
      setExceptionReason("");
      onQuoteChanged?.();
      checkExistingConversion();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConverting(false);
    }
  };

  const openShipment = () => {
    if (!shipmentId) return;
    // Deeplink naar admin > warehouse > ShipmentDetail
    const url = `/admin?section=warehouse&shipment_id=${shipmentId}`;
    window.location.hash = "";
    window.location.href = url;
  };

  return (
    <div className="space-y-6" data-testid="quote-conversion-panel">
      {/* Stap 1 — Klantkoppeling */}
      <SectionCard
        step="1"
        title="Klantkoppeling"
        icon={<UserCheck className="h-4 w-4" />}
        testId="conversion-section-customer"
      >
        {linkedCustomer ? (
          <div className="space-y-2" data-testid="linked-customer-view">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium text-slate-900">
                  {linkedCustomer.display_name ||
                    `${linkedCustomer.first_name || ""} ${linkedCustomer.last_name || ""}`.trim()}
                </div>
                {linkedCustomer.company_name && (
                  <div className="text-xs text-slate-500">
                    {linkedCustomer.company_name}
                  </div>
                )}
                <div className="text-sm text-slate-700 mt-1">
                  {linkedCustomer.email}
                  {linkedCustomer.phone ? ` · ${linkedCustomer.phone}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800 border-green-300">
                  Gekoppeld
                </Badge>
                {canManageCustomerLink && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMatchDialogOpen(true)}
                    data-testid="btn-change-customer-link"
                  >
                    Wijzigen
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Alert
              className="border-amber-300 bg-amber-50"
              data-testid="linked-customer-missing-alert"
            >
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-amber-900 text-sm">
                Geen klant gekoppeld — koppel een klant voordat u de offerte omzet
                naar een zending (verplicht conform businessregel).
              </AlertDescription>
            </Alert>
            {canManageCustomerLink ? (
              <Button
                onClick={() => setMatchDialogOpen(true)}
                data-testid="btn-start-customer-match"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Klantmatching starten
              </Button>
            ) : (
              <p className="text-xs text-slate-500" data-testid="linked-customer-readonly-hint">
                Alleen Manager/Admin/Owner kunnen klantmatching starten.
              </p>
            )}
          </div>
        )}
      </SectionCard>

      {/* Stap 2 — Conversie naar shipment */}
      <SectionCard
        step="2"
        title="Conversie naar zending"
        icon={<Truck className="h-4 w-4" />}
        testId="conversion-section-shipment"
      >
        {shipmentId ? (
          <div className="space-y-3" data-testid="conversion-existing">
            <Alert
              className="border-green-300 bg-green-50"
              data-testid="conversion-existing-alert"
            >
              <Truck className="h-4 w-4 text-green-700" />
              <AlertDescription className="text-green-900 text-sm">
                Deze offerte is reeds geconverteerd naar een concept-zending.
                {existingShipment?.tracking_number && (
                  <> Tracking: <code>{existingShipment.tracking_number}</code></>
                )}
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={openShipment}
                data-testid="btn-open-shipment"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Zending openen
              </Button>
              {canForceException && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExceptionOpen(true)}
                  data-testid="btn-open-exception-copy"
                >
                  <ArrowRight className="h-4 w-4 mr-1" />
                  Exception-copy maken
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={checkExistingConversion}
                disabled={checkingExisting}
                data-testid="btn-refresh-shipment"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Ververs
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Zet deze offerte om in een operationele concept-zending. De backend
              berekent klantmatching, colli-mapping, bestemming en pricing-facade
              — deze UI toont enkel het resultaat.
            </p>
            <Button
              onClick={() => handleConvert(false)}
              disabled={!canConvert || !quote?.customer_id || converting}
              data-testid="btn-convert-to-shipment"
            >
              {converting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Bezig…
                </>
              ) : (
                <>
                  <Truck className="h-4 w-4 mr-2" />
                  Converteren naar zending
                </>
              )}
            </Button>
            {!canConvert && (
              <p className="text-xs text-slate-500">
                U heeft geen rechten om deze conversie uit te voeren.
              </p>
            )}
          </div>
        )}
      </SectionCard>

      {/* Stap 3 — Mapping review */}
      {shipmentId && (
        <SectionCard
          step="3"
          title="Mapping controle"
          icon={<Info className="h-4 w-4" />}
          testId="conversion-section-mapping"
        >
          <QuoteMappingReview
            shipmentId={shipmentId}
            initialData={convertResult}
            onRefresh={checkExistingConversion}
          />
        </SectionCard>
      )}

      {/* Dialogs */}
      <CustomerMatchCandidatesDialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
        quoteId={quoteId}
        onLinked={() => {
          setMatchDialogOpen(false);
          onQuoteChanged?.();
        }}
      />

      <Dialog open={exceptionOpen} onOpenChange={setExceptionOpen}>
        <DialogContent data-testid="exception-copy-dialog">
          <DialogHeader>
            <DialogTitle>Exception-copy shipment aanmaken</DialogTitle>
          </DialogHeader>
          <Alert className="border-amber-300 bg-amber-50 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900 text-sm">
              Er bestaat al een primary shipment. Een exception-copy wordt alleen
              gebruikt voor uitzonderingen (bv. gesplitste verzendingen). Enkel
              Owner/Admin mogen dit uitvoeren en een reden is verplicht.
            </AlertDescription>
          </Alert>
          <Label className="text-xs text-slate-600 mb-1 block">
            Reden voor exception-copy <span className="text-red-500">*</span>
          </Label>
          <Textarea
            value={exceptionReason}
            onChange={(e) => setExceptionReason(e.target.value)}
            placeholder="Bijvoorbeeld: gesplitste verzending, klant heeft 2e zending goedgekeurd..."
            data-testid="exception-copy-reason"
            rows={3}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExceptionOpen(false)}
              data-testid="exception-copy-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={() => handleConvert(true, exceptionReason)}
              disabled={!exceptionReason.trim() || converting}
              data-testid="exception-copy-submit"
            >
              {converting ? "Bezig…" : "Exception-copy maken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionCard({ step, title, icon, children, testId }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">
          {step}
        </div>
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          {icon}
          {title}
        </div>
      </div>
      {children}
    </div>
  );
}
